import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SchedoraLogo } from "@/components/schedora/SchedoraLogo";
import { useApp } from "@/context/AppContext";
import { supabase } from "@/lib/supabase";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { updatePassword } = useApp();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recoveryReady, setRecoveryReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const establishRecoverySession = async () => {
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const accessToken = hash.get("access_token");
      const refreshToken = hash.get("refresh_token");
      const type = hash.get("type");

      try {
        if (type === "recovery" && accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            throw sessionError;
          }
        } else {
          const { data } = await supabase.auth.getSession();
          if (!data.session) {
            throw new Error("Your reset link is missing or expired. Please request a new one.");
          }
        }

        if (!cancelled) {
          setRecoveryReady(true);
          window.history.replaceState({}, document.title, "/reset-password");
        }
      } catch (nextError) {
        if (!cancelled) {
          setError(nextError instanceof Error ? nextError.message : "Unable to verify your reset link.");
        }
      }
    };

    void establishRecoverySession();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    if (!recoveryReady) {
      setError("Your reset link is still being verified. Please wait a moment.");
      return;
    }

    if (password.length < 8) {
      setError("Use at least 8 characters for the new password.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    const result = await updatePassword(password);
    if (!result.ok) {
      setError(result.message);
      setIsSubmitting(false);
      return;
    }

    setError("");
    setSaved(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <SchedoraLogo textClassName="text-xl" />
        </Link>
        {!saved ? (
          <>
            <h1 className="text-2xl font-heading font-bold text-foreground">Create a new password</h1>
            <p className="mt-1 text-sm text-muted-foreground">Choose a secure password for your Schedora account.</p>
            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div>
                <Label htmlFor="password">New password</Label>
                <Input id="password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="confirmPassword">Confirm new password</Label>
                <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} className="mt-1" />
              </div>
              {!recoveryReady && !error && <p className="text-sm text-muted-foreground">Verifying your reset link…</p>}
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button className="w-full" type="submit" disabled={isSubmitting || !recoveryReady}>
                {isSubmitting ? "Saving..." : "Save New Password"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success/10 mb-4">
              <CheckCircle2 className="h-6 w-6 text-success" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground">Password updated</h1>
            <p className="mt-2 text-sm text-muted-foreground">You can sign in now with your new password.</p>
            <Button className="mt-6" onClick={() => navigate("/login")}>Go to Login</Button>
          </div>
        )}
        <Link to="/login" className="mt-6 inline-flex items-center gap-1 text-sm text-primary hover:underline">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to login
        </Link>
      </div>
    </div>
  );
}
